import api, { getErrorMessage } from './api';

const nominationsService = {
  /**
   * Get nominations sent by current user
   * @returns {Promise<Array>} - Sent nominations list
   */
  getMyNominations: async () => {
    try {
      const response = await api.get('/api/v1/nominations/');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get nominations received by current user
   * @returns {Promise<Array>} - Received nominations list
   */
  getReceivedNominations: async () => {
    try {
      const response = await api.get('/api/v1/nominations/received');
      return response.data;
    } catch (error) {
      // Return empty array if endpoint doesn't exist yet
      console.warn('Received nominations endpoint not available:', error.message);
      return [];
    }
  },

  /**
   * Get active nomination campaigns
   * @returns {Promise<Array>} - Active campaigns list
   */
  getActiveCampaigns: async () => {
    try {
      const response = await api.get('/api/v1/nominations/campaigns/active');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get awards for a campaign (user-facing)
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Array>} - Campaign awards list
   */
  getActiveCampaignAwards: async (campaignId) => {
    try {
      const response = await api.get(`/api/v1/nominations/campaigns/${campaignId}/awards`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Create a new nomination
   * @param {object} nominationData - Nomination data
   * @param {string} nominationData.recipient_id - Nominee user ID
   * @param {string} nominationData.campaign_id - Campaign ID
   * @param {string} nominationData.message - Nomination message
   * @param {boolean} nominationData.post_to_workvivo - Whether to post to Workvivo
   * @param {string} nominationData.workvivo_space_id - Workvivo space ID (optional)
   * @param {string} nominationData.workvivo_message_override - Custom Workvivo message (optional)
   * @returns {Promise<object>} - Created nomination
   */
  createNomination: async (nominationData) => {
    try {
      const response = await api.post('/api/v1/nominations/', nominationData);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  updateNomination: async (nominationId, body) => {
    try {
      const response = await api.patch(`/api/v1/nominations/${nominationId}`, body);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  deleteNomination: async (nominationId) => {
    try {
      const response = await api.delete(`/api/v1/nominations/${nominationId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get all nominations (admin only)
   * @param {number} skip - Number of records to skip
   * @param {number} limit - Number of records to return
   * @returns {Promise<object>} - Paginated nominations list
   */
  getAllNominations: async (skip = 0, limit = 100) => {
    try {
      const response = await api.get('/api/v1/admin/nominations/', {
        params: { skip, limit },
      });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get nomination by ID (admin only)
   * @param {string} nominationId - Nomination ID
   * @returns {Promise<object>} - Nomination data
   */
  getNominationById: async (nominationId) => {
    try {
      const response = await api.get(`/api/v1/admin/nominations/${nominationId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get all nomination campaigns (admin only)
   * @param {string} search - Optional search query for campaign name
   * @returns {Promise<Array>} - All campaigns list
   */
  getAllCampaigns: async (search = null) => {
    try {
      const params = {};
      if (search) {
        params.search = search;
      }
      const response = await api.get('/api/v1/admin/nominations/campaigns', { params });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Create a new nomination campaign (admin only)
   * @param {object} campaignData - Campaign data
   * @returns {Promise<object>} - Created campaign
   */
  createCampaign: async (campaignData) => {
    try {
      const response = await api.post('/api/v1/admin/nominations/campaigns', campaignData);
      return response.data;
    } catch (error) {
      // Preserve the original axios error so formatError can extract the detail
      throw error;
    }
  },

  /**
   * Get campaign by ID with nominations (admin only)
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<object>} - Campaign data with nominations
   */
  getCampaignById: async (campaignId) => {
    try {
      const response = await api.get(`/api/v1/admin/nominations/campaigns/${campaignId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get campaign statistics (admin only)
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<object>} - Campaign statistics
   */
  getCampaignStats: async (campaignId) => {
    try {
      const response = await api.get(`/api/v1/admin/nominations/campaigns/${campaignId}/stats`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Update campaign status (admin only)
   * @param {string} campaignId - Campaign ID
   * @param {boolean} isActive - Whether campaign is active
   * @returns {Promise<object>} - Updated campaign
   */
  updateCampaignStatus: async (campaignId, isActive) => {
    try {
      const response = await api.patch(`/api/v1/admin/nominations/campaigns/${campaignId}`, {
        is_active: isActive,
      });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Update campaign details (admin only)
   * @param {string} campaignId - Campaign ID
   * @param {object} campaignData - Campaign data to update
   * @returns {Promise<object>} - Updated campaign
   */
  updateCampaign: async (campaignId, campaignData) => {
    try {
      const response = await api.patch(`/api/v1/admin/nominations/campaigns/${campaignId}`, campaignData);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Delete a campaign (admin only)
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<object>} - Success message
   */
  deleteCampaign: async (campaignId) => {
    try {
      const response = await api.delete(`/api/v1/admin/nominations/campaigns/${campaignId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * End a campaign (admin only)
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<object>} - Success message
   */
  endCampaign: async (campaignId) => {
    try {
      const response = await api.post(`/api/v1/admin/nominations/campaigns/${campaignId}/end`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Pause a campaign (admin only)
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<object>} - Success message
   */
  pauseCampaign: async (campaignId) => {
    try {
      const response = await api.post(`/api/v1/admin/nominations/campaigns/${campaignId}/pause`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get campaign awards (admin only)
   * @param {string} campaignId - Campaign ID
   * @param {boolean} includeInactive - Include inactive awards
   * @returns {Promise<object>} - Campaign awards
   */
  getCampaignAwards: async (campaignId, includeInactive = false) => {
    try {
      const response = await api.get(`/api/v1/admin/nominations/campaigns/${campaignId}/awards`, {
        params: { include_inactive: includeInactive },
      });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Create a campaign award (admin only)
   * @param {string} campaignId - Campaign ID
   * @param {object} awardData - Award data
   * @returns {Promise<object>} - Created award
   */
  createCampaignAward: async (campaignId, awardData) => {
    try {
      const response = await api.post(`/api/v1/admin/nominations/campaigns/${campaignId}/awards`, awardData);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Update a campaign award (admin only)
   * @param {string} campaignId - Campaign ID
   * @param {string} awardId - Award ID
   * @param {object} awardData - Award data
   * @returns {Promise<object>} - Updated award
   */
  updateCampaignAward: async (campaignId, awardId, awardData) => {
    try {
      const response = await api.put(`/api/v1/admin/nominations/campaigns/${campaignId}/awards/${awardId}`, awardData);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Delete a campaign award (admin only)
   * @param {string} campaignId - Campaign ID
   * @param {string} awardId - Award ID
   * @returns {Promise<object>} - Success message
   */
  deleteCampaignAward: async (campaignId, awardId) => {
    try {
      const response = await api.delete(`/api/v1/admin/nominations/campaigns/${campaignId}/awards/${awardId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get per-award leaderboard with rankings (admin only)
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<object>} - Leaderboard data
   */
  getCampaignLeaderboard: async (campaignId) => {
    try {
      const response = await api.get(`/api/v1/admin/nominations/campaigns/${campaignId}/leaderboard`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Select a winner for an award (admin only)
   * @param {string} campaignId - Campaign ID
   * @param {string} awardId - Award ID
   * @param {string} userId - Winner user ID
   * @param {string} adminNote - Optional admin note
   * @returns {Promise<object>} - Updated award
   */
  selectAwardWinner: async (campaignId, awardId, userId, adminNote = null) => {
    try {
      const response = await api.post(
        `/api/v1/admin/nominations/campaigns/${campaignId}/awards/${awardId}/select-winner`,
        { user_id: userId, admin_note: adminNote }
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Clear a selected winner before finalization (admin only)
   * @param {string} campaignId - Campaign ID
   * @param {string} awardId - Award ID
   * @returns {Promise<object>} - Updated award
   */
  clearAwardWinner: async (campaignId, awardId) => {
    try {
      const response = await api.post(
        `/api/v1/admin/nominations/campaigns/${campaignId}/awards/${awardId}/clear-winner`
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Finalize campaign: award points to all selected winners (admin only)
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<object>} - Finalization results
   */
  finalizeCampaign: async (campaignId) => {
    try {
      const response = await api.post(
        `/api/v1/admin/nominations/campaigns/${campaignId}/finalize`
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },
};

// Aliases for compatibility
nominationsService.listAllCampaigns = nominationsService.getAllCampaigns;
nominationsService.listActiveCampaigns = nominationsService.getActiveCampaigns;
nominationsService.create = nominationsService.createNomination;
nominationsService.listMine = nominationsService.getMyNominations;
nominationsService.listReceived = nominationsService.getReceivedNominations;

export default nominationsService;
